<template>
  <v-row ref="resultadoConsulta" class="ma-0" align="start">
    <ExpansionPanel
      v-model="pageSubfiltros"
      :show-filter="store.showFilter"
      :class="{ 'mr-3': store.showFilter }"
      @close="store.showFilter = false"
      @limpar="limparFiltrosResultado"
      @filtrar="consulta"
    />

    <v-col class="pa-0">
      <v-row class="ma-0" align="center">
        <v-expand-transition>
          <v-btn
            v-if="!store.showFilter"
            outlined
            color="black"
            class="pa-0 mr-4"
            style="min-width: 35px"
            @click="store.showFilter = true"
          >
            <v-icon>
              {{ mdiFilterVariant }}
            </v-icon>
          </v-btn>
        </v-expand-transition>
        <v-card-title class="pa-0"> Resultado da consulta </v-card-title>
      </v-row>

      <AlertCard
        class="mt-3"
        :title="`${totalHits} registros encontrados`"
        :content="
          totalHits > 9999
            ? `O número de registros retornados excede o limite (10000). Por favor,
          refine a consulta utilizando mais filtros.`
            : ''
        "
      />

      <v-row class="ma-0" justify="space-between" align="end">
        <v-col class="d-flex align-end pl-0">
          <v-checkbox
            hide-details
            class="ma-0"
            label="Selecionar todos"
            :value="
              isTodosSelecionados(
                jurisprudencias,
                jurisprudenciasSelecionadas,
                'cdJurisprudencia'
              )
            "
            @change="
              jurisprudenciasSelecionadas = selecionarTodos(
                jurisprudencias,
                jurisprudenciasSelecionadas,
                'cdJurisprudencia'
              )
            "
          />
        </v-col>
        <v-row class="ma-0" justify="end" align="end">
          <FiltroOrdenacao
            v-model="ordenacaoSelecionada"
            :items="ordenacao"
            @change="atualizarOrdenacao"
          />
          <div class="pa-3 pr-0">
            <SeletorOpcoesImpressão
              :disabled="desabilitarImpressao"
              :items="itensImpressao"
              @change="download"
            />
          </div>
        </v-row>
      </v-row>

      <v-row class="ma-0">
        <v-col class="pa-0" cols="12">
          <v-data-table
            hide-default-footer
            hide-default-header
            hide-body
            :items="jurisprudencias"
            :server-items-length="totalElementos"
            item-key="id"
            no-data-text="Nenhum registro encontrado"
            style="background: #e5e5e5"
          >
            <template #body>
              <div>
                <CardResultado
                  v-for="(item, index) in jurisprudencias"
                  :key="'item-' + index"
                  v-model="item.selecionadoParaImpressao"
                  :jurisprudencia="item"
                  class="mb-4"
                  :is-selecionado="
                    contemNaLista(
                      jurisprudenciasSelecionadas,
                      item,
                      'cdJurisprudencia'
                    )
                  "
                  @toggle-check="
                    jurisprudenciasSelecionadas = toggleSelecionado(
                      jurisprudenciasSelecionadas,
                      item,
                      'cdJurisprudencia'
                    )
                  "
                />
              </div>
            </template>
            <template #footer="{ props }">
              <div class="p-4" style="background: white; border-radius: 6px">
                <v-pagination
                  v-model="pagina"
                  :length="props.pagination.pageCount"
                  :total-visible="9"
                  @input="atualizarPaginacao"
                ></v-pagination>
              </div>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import ExpansionPanel from '@/views/components/ExpansionPanel.vue';
import AlertCard from '@/views/components/AlertCard.vue';
import { mdiFilterVariant } from '@mdi/js';
import FiltroOrdenacao from '@/views/components/FiltroOrdenacao.vue';
import SeletorOpcoesImpressão from '@/views/components/SeletorOpcoesImpressão.vue';
import CardResultado from './CardResultado.vue';
import { ConsultaJurisprudenciaSelecionadaStore } from '../../store/ConsultaJurisprudenciaSelecionadaStore';
import { storeToRefs } from 'pinia';
import IconPaginaSimplificada from '@/views/components/icons/IconPaginaSimplificada.vue';
import IconPaginaDetalhada from '@/views/components/icons/IconPaginaDetalhada.vue';
import IconTextoNorma from '@/views/components/icons/IconTextoNorma.vue';
import { contemNaLista, toggleSelecionado } from '@/utils/ListUtils';
import { isTodosSelecionados, selecionarTodos } from '@/utils/ListUtils';
const store = ConsultaJurisprudenciaSelecionadaStore();

const {
  pageSubfiltros,
  jurisprudencias,
  totalElementos,
  pagina,
  jurisprudenciasSelecionadas,
  totalHits,
  filtroJurisprudencia,
} = storeToRefs(store);

const { consultaJurisprudenciaSelecionada } = store;

const resultadoConsulta = ref();

const { download } = store;

const itensImpressao = [
  {
    text: 'Acórdãos',
    value: 'A',
    icone: IconTextoNorma,
  },
  {
    text: 'Simplificado',
    value: 'S',
    icone: IconPaginaSimplificada,
  },
  {
    text: 'Detalhado',
    value: 'D',
    icone: IconPaginaDetalhada,
  },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const atualizarOrdenacao = async (ordenacao: any) => {
  ordenacaoSelecionada.value = ordenacao;
  filtroJurisprudencia.value.ordenacao = ordenacao.value;
  await consultaJurisprudenciaSelecionada();
};

const ordenacao = [
  {
    text: 'Data de julgamento decrescente',
    value: 'dtjulgamento desc',
  },
  { text: 'Data de julgamento crescente', value: 'dtjulgamento asc' },
  { text: 'Relevância', value: 'deRelevancia' },
];

const ordenacaoSelecionada = ref({
  text: 'Data de julgamento decrescente',
  value: 'dtjulgamento desc',
});

const desabilitarImpressao = computed(() => {
  return jurisprudenciasSelecionadas.value?.length < 1;
});

const consulta = () => {
  pagina.value = 1;
  consultaJurisprudenciaSelecionada();
};

const atualizarPaginacao = async () => {
  resultadoConsulta.value.scrollIntoView({ behavior: 'smooth' });
  await consultaJurisprudenciaSelecionada();
};

const limparFiltrosResultado = () => {
  pageSubfiltros.value?.forEach((filtro) =>
    filtro?.valoresSubFiltroResponseDTO?.forEach(
      (subFiltro) => (subFiltro.checked = false)
    )
  );
};
</script>

<style lang="scss" scoped>
::v-deep .v-data-table {
  table,
  .v-data-footer {
    border: none !important;

    .v-data-footer__select {
      margin-right: 0 !important;
    }
  }

  .container {
    padding-left: 8px !important;
  }
}
</style>
